import { Icon, system } from '@hover/blueprint';
import type { ButtonProps } from '@hover/blueprint';
import { iconEdit } from '@hover/icons';
import React from 'react';
import { Link } from 'react-router-dom';

import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import { Button } from 'src/shared/components';
import type { AiDisplayImage } from 'src/shared/graphql/generated';
import { preserveNativeLinking } from 'src/shared/helpers/routing';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { useButtonImpression } from 'src/shared/hooks/useButtonImpression/useButtonImpression';

import { disableLoaderSlide } from '../../helpers/styles';
import { useCustomizeHandler } from '../../hooks';

export interface CustomizeButtonProps extends ButtonProps {
  image: AiDisplayImage;
  trackingData: {
    track_id: string;
    view_type: string;
  };
}

const className = 'customize-button';
const iconClassName = 'customize-button-icon';
const textClassName = 'customize-button-text';

/**
 * These selectors are made available in order to target parts of this component
 * from higher up in the DOM tree when we can't use more idiomatic Chakra or
 * Emotion styling techniques (i.e: when using a container query)
 *
 * @see {@link ../GeneratedImageThumbnail.tsx} for an example
 */
export const selectors = {
  root: `.${className}`,
  icon: `.${className} .${iconClassName}`,
  text: `.${className} .${textClassName}`,
};

const CustomizeButton = ({
  className: classNameProp,
  image,
  sx,
  trackingData,
  ...props
}: CustomizeButtonProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

  const { trackingData: contextTrackingData } = useHomeownerAppContext();
  const customizeText = t('paint.customize');

  const { onClick, tracking, customizePathname } = useCustomizeHandler({
    image,
  });

  const augmentedTrackingData = {
    ...contextTrackingData,
    ...trackingData,
    button_text: customizeText,
    primary_cta: false,
    space_type: image.spaceType ?? undefined,
    ...tracking,
  };

  useButtonImpression(augmentedTrackingData);

  const handleClick = async (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    event.stopPropagation();

    await preserveNativeLinking(event, onClick);
  };

  return (
    <Button
      as={Link}
      className={[classNameProp, className].filter(Boolean).join(' ')}
      label={customizeText}
      onClick={e => handleClick(e)}
      optionalTrackingProperties={augmentedTrackingData}
      sx={{ ...disableLoaderSlide, ...sx }}
      to={customizePathname}
      {...props}
    >
      <system.span className={textClassName}>{customizeText}</system.span>
      <Icon className={iconClassName} display="none" icon={iconEdit} isInline />
    </Button>
  );
};

export { CustomizeButton };
